import { useTranslations } from "use-intl";

import { CloseIcon, FilterIcon, SearchIcon, SortIcon, StyleIcon } from "@/components/appIcons";
import { ArtistFilterSelect } from "@/components/artist-filter-select";
import { CountryFilterSelect } from "@/components/country-filter-select";
import { GenreFilterSelect } from "@/components/genre-filter-select";
import { FilterSelect, type FilterOption } from "@/components/ui/filter-select";
import { FilterToolbarControl, FilterToolbarControls } from "@/components/ui/filter-toolbar";

import type { CollectionSort, CollectionStatusFilter } from "./collection-grid-options";

export function CollectionGridToolbar({
  artist,
  artistCountry,
  artistCountryOptions,
  artistOptions,
  genre,
  genreOptions,
  hasActiveFilters,
  isAuthenticated,
  onArtistChange,
  onArtistCountryChange,
  onClearFilters,
  onGenreChange,
  onQueryChange,
  onSortChange,
  onStatusChange,
  onStyleChange,
  query,
  resultCount,
  sort,
  sortOptions,
  status,
  statusOptions,
  style,
  styleOptions,
  totalCount,
}: {
  artist: string | null;
  artistCountry: string | null;
  artistCountryOptions: FilterOption[];
  artistOptions: FilterOption[];
  genre: string | null;
  genreOptions: FilterOption[];
  hasActiveFilters: boolean;
  isAuthenticated: boolean;
  onArtistChange: (value: string | null) => void;
  onArtistCountryChange: (value: string | null) => void;
  onClearFilters: () => void;
  onGenreChange: (value: string | null) => void;
  onQueryChange: (value: string) => void;
  onSortChange: (value: CollectionSort) => void;
  onStatusChange: (value: CollectionStatusFilter) => void;
  onStyleChange: (value: string | null) => void;
  query: string;
  resultCount: number;
  sort: CollectionSort;
  sortOptions: FilterOption[];
  status: CollectionStatusFilter;
  statusOptions: FilterOption[];
  style: string | null;
  styleOptions: FilterOption[];
  totalCount: number;
}) {
  const t = useTranslations("featureCollections");
  const filterT = useTranslations("shared.releaseFilters");

  return (
    <div className="mt-8 space-y-3 border-b border-border pb-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative min-w-0 flex-1">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[18px] text-muted-foreground" />
          <input
            aria-label={t("grid.searchLabel")}
            className="h-11 w-full rounded-md border border-border bg-secondary/50 pl-9 pr-8 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50 sm:h-10"
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={t("grid.searchPlaceholder")}
            type="text"
            value={query}
          />
          {query ? (
            <button
              aria-label={t("grid.clearFilters")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => onQueryChange("")}
              type="button"
            >
              <CloseIcon className="text-[16px]" />
            </button>
          ) : null}
        </div>
        <FilterSelect
          className="hidden sm:inline-block"
          clearable={false}
          icon={SortIcon}
          intent="sort"
          onChange={(value) => value && onSortChange(value as CollectionSort)}
          options={sortOptions}
          placeholder={t("grid.sortLabel")}
          triggerClassName="sm:min-w-[220px]"
          triggerLabelClassName="max-w-none"
          value={sort}
        />
      </div>

      <FilterToolbarControls label={filterT("filterBy")}>
        {genreOptions.length > 0 ? (
          <FilterToolbarControl>
            <GenreFilterSelect onChange={onGenreChange} options={genreOptions} value={genre} />
          </FilterToolbarControl>
        ) : null}
        {styleOptions.length > 0 ? (
          <FilterToolbarControl>
            <FilterSelect
              icon={StyleIcon}
              onChange={onStyleChange}
              options={styleOptions}
              placeholder={t("grid.styleLabel")}
              searchable
              value={style}
            />
          </FilterToolbarControl>
        ) : null}
        {artistOptions.length > 0 ? (
          <FilterToolbarControl spanClassName="col-span-2 sm:col-auto">
            <ArtistFilterSelect onChange={onArtistChange} options={artistOptions} value={artist} />
          </FilterToolbarControl>
        ) : null}
        {artistCountryOptions.length > 0 ? (
          <FilterToolbarControl>
            <CountryFilterSelect
              onChange={onArtistCountryChange}
              options={artistCountryOptions}
              placeholder={t("grid.artistCountryLabel")}
              value={artistCountry}
            />
          </FilterToolbarControl>
        ) : null}
        {isAuthenticated ? (
          <FilterToolbarControl>
            <FilterSelect
              icon={FilterIcon}
              onChange={(value) => onStatusChange((value ?? "all") as CollectionStatusFilter)}
              options={statusOptions}
              placeholder={t("grid.statusLabel")}
              value={status === "all" ? null : status}
            />
          </FilterToolbarControl>
        ) : null}
        {hasActiveFilters ? (
          <button
            className="col-span-2 flex h-11 items-center justify-center gap-1.5 rounded-md border border-border px-3 text-sm text-muted-foreground transition-colors hover:text-foreground sm:col-auto sm:h-auto sm:justify-start sm:rounded-none sm:border-0 sm:px-2"
            onClick={onClearFilters}
            type="button"
          >
            <CloseIcon className="text-[16px]" />
            {t("grid.clearFilters")}
          </button>
        ) : null}
        <span
          aria-live="polite"
          className="col-span-2 text-right text-xs text-muted-foreground sm:col-auto sm:ml-auto sm:text-left"
        >
          {t("grid.resultsCount", { shown: resultCount, total: totalCount })}
        </span>
      </FilterToolbarControls>

      <FilterSelect
        className="sm:hidden"
        clearable={false}
        icon={SortIcon}
        intent="sort"
        onChange={(value) => value && onSortChange(value as CollectionSort)}
        options={sortOptions}
        placeholder={t("grid.sortLabel")}
        triggerClassName="w-full"
        triggerLabelClassName="max-w-none"
        value={sort}
      />
    </div>
  );
}
